import React, { Fragment, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Grid, Text } from 'elements';
import MentorFilterModal from 'components/modal/MentorFilterModal';
import { getLibraryList, bookmarkLibrary } from 'libs/api/case';
import { getUrlQuery } from 'libs/utils';
import { TRequestLibraryList, TLibraryList } from 'modules/case/types';
import { TModalState } from 'modules/types';
import { CWindowWidth } from 'constants/base.const';
import { EmployedTypes } from 'enums/base.enum';
import { Button, Select, Pagination, Spin, Input } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { Colors, LightGrayColor } from 'GlobalStyles';
import CheckedSource from 'assets/icons/FavoriteChecked.webp';
import UncheckedSource from 'assets/icons/FavoriteUnchecked.webp';
import MainBackImage from 'assets/images/banners/4.webp';

type TLibraryFilter = {
  company?: string;
  hired_type?: string;
  location?: string;
  interview?: string;
};

type TQuery = {
  page?: string;
  order?: string;
  selected?: string;
  is_mine?: string;
  company?: string;
  hired_type?: string;
  location?: string;
  interview?: string;
  search?: string;
};

let timeout: number = 0;
const Library = () => {
  const location = useLocation();
  const queries: TQuery = getUrlQuery(location.search);
  const history = useHistory();
  const limit = 10;
  const [list, setList] = useState<Array<TLibraryList>>();
  const [page, setPage] = useState<number>(queries.page ? Number(queries.page) : 1);
  const [total, setTotal] = useState<number>(1);
  const [selectedOrder, setSelectedOrder] = useState(queries.order || 'new');
  const [selected, setSelected] = useState(queries.selected || 'all');
  const [mentorFilterModal, setMentorFilterModal] = useState<TModalState>({
    record: '',
    visible: false,
  });
  const [isMine, setIsMine] = useState(queries.is_mine || false);
  const [filter, setFilter] = useState<TLibraryFilter>({
    company: queries.company,
    hired_type: queries.hired_type,
    location: queries.location,
    interview: queries.interview,
  });
  const [search, setSearch] = useState(queries.search || '');

  const openFilter = () => {
    setMentorFilterModal({
      visible: true,
      type: 'library',
      onFinish: ({ company, hired_type, location, interview }: TLibraryFilter) => {
        setFilter({ company, hired_type, location, interview });
      },
    });
  };

  const getList = async () => {
    const replaceAll = (str: string, target: string, replace: string) => {
      return str.split(target).join(replace);
    };
    const params: any = {
      current: page,
      pageSizes: limit,
      bookmark: selected === 'bookmark' ? 'bookmark' : undefined,
      my_library: isMine ? 'my_library' : undefined,
      q: search || undefined,
    };
    if (filter) {
      Object.keys(filter).forEach((key: string) => {
        if (filter[key as keyof TLibraryFilter])
          params[key] = replaceAll(filter[key as keyof TLibraryFilter] || '', '+', '*');
      });
    }
    params[selectedOrder] = selectedOrder;
    //qna 리스트로 바꾸기
    const { success, data } = await getLibraryList(params as TRequestLibraryList);
    if (success) {
      setTotal(data.total);
      setList(
        data.contents.map((content: any) => {
          if (content.hired_type) {
            const finded = EmployedTypes.find((t) => t.value === content.hired_type);
            if (finded) content.hired_type_text = finded.text;
          }
          return content;
        }),
      );
    }
  };

  const routeQuery = () => {
    const newQuries = [`page=${page}`, `order=${selectedOrder}`];
    if (filter) {
      Object.keys(filter).forEach((key: string) => {
        if (filter[key as keyof TLibraryFilter])
          newQuries.push(`${key}=${filter[key as keyof TLibraryFilter]}`);
      });
    }
    if (isMine) newQuries.push('is_mine=true');
    if (selected) newQuries.push(`selected=${selected}`);
    if (search) newQuries.push(`search=${search}`);
    history.replace(`/case/library?${newQuries.join('&')}`);
    getList();
  };

  const bookmark = async (id: number) => {
    const { success } = await bookmarkLibrary(id);
    if (success) getList();
  };

  useEffect(() => {
    if (list) {
      if (timeout) clearTimeout(timeout);
      timeout = setTimeout(routeQuery, 500);
    }
  }, [search]);

  useEffect(() => {
    if (list) {
      if (page === 1) {
        routeQuery();
      } else setPage(1);
    }
  }, [selected]);

  useEffect(() => {
    routeQuery();
  }, [filter, isMine, page, selectedOrder]);

  return (
    <>
      <Grid image={MainBackImage} height='400px' md={{ height: '100px' }}>
        <Grid height='100%' backgroundColor={`${Colors.black}33`} justify='center'>
          <Grid width={`${CWindowWidth}px`} padding='0 7%' align='center'>
            <Grid>
              <Text color={Colors.white} size='4rem' bold md={{ size: '2rem' }}>
                Case 기출 라이브러리
              </Text>
            </Grid>
            <Grid marginTop='5px'>
              <Text color={Colors.white} size='1.1rem' md={{ size: '0.9rem' }}>
                Case 인터뷰 실제 기출문제 게시판입니다.
              </Text>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Grid
        isNoWrap
        justify='center'
        gap='20px'
        padding='20px'
        style={{ borderBottom: `1px solid ${LightGrayColor}` }}
      >
        <Button
          type='text'
          style={{ fontSize: '1.3rem', width: 130 }}
          className={'btn-text' + `${selected !== 'all' ? ' disabled' : ''}`}
          onClick={() => setSelected('all')}
        >
          모두 보기
        </Button>
        <Button
          type='text'
          style={{ fontSize: '1.3rem', width: 130 }}
          className={'btn-text' + `${selected !== 'bookmark' ? ' disabled' : ''}`}
          onClick={() => setSelected('bookmark')}
        >
          찜한 Case
        </Button>
      </Grid>
      <Grid justify='center'>
        <Grid width={`${CWindowWidth}px`} padding='38px 20px 150px 20px'>
          <Grid justify='space-between' align='center'>
            <Grid isInline width='300px'>
              <Text size='1.1rem'>총 124개 Case</Text>
            </Grid>
            <Grid isInline maxWidth='280px' sm={{ maxWidth: '100%', width: '100%', marginTop: '8px' }}>
              <Input
                size='large'
                placeholder='검색어 입력'
                prefix={<SearchOutlined style={{ color: Colors.grey2 }} />}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </Grid>
            <Grid isInline width='300px' gap='16px' justify='flex-end' sm={{ display: 'none' }}>
              <Button onClick={openFilter}>필터</Button>
              <Select value={selectedOrder} onChange={setSelectedOrder}>
                <Select.Option value={'new'}>최신순</Select.Option>
                <Select.Option value={'view'}>조회순</Select.Option>
                <Select.Option value={'comment'}>댓글순</Select.Option>
              </Select>
            </Grid>
          </Grid>
          <Grid justify='flex-end' marginTop='16px' sm={{ display: 'none' }}>
            <Grid isInline gap='16px' align='center'>
              <Button className='text large' onClick={() => setIsMine(!isMine)}>
                {isMine ? '전체' : '내'} 글 보기
              </Button>
              <Button className='outline large' onClick={() => history.push('/case/library/write')}>
                글쓰기
              </Button>
            </Grid>
          </Grid>
          <Grid
            display='none'
            justify='space-between'
            align='center'
            marginTop='12px'
            sm={{ display: 'flex' }}
          >
            <Button className='text' onClick={() => setIsMine(!isMine)}>
              <Text textDecoration='underline'>{isMine ? '전체' : '내'} 글 보기</Text>
            </Button>
            <Grid isInline gap='8px'>
              <Button onClick={openFilter}>필터</Button>
              <Select value={selectedOrder} onChange={setSelectedOrder}>
                <Select.Option value={'numbering'}>최신순</Select.Option>
                <Select.Option value={'views'}>조회순</Select.Option>
                <Select.Option value={'difficulty'}>댓글순</Select.Option>
              </Select>
            </Grid>
          </Grid>

          <Grid marginTop='16px'>
            {!list ? (
              <Grid justify='center' align='center' height='100px'>
                <Spin spinning />
              </Grid>
            ) : list.length === 0 ? (
              <Grid justify='center' align='center' height='100px'>
                <Text size='1.2rem'>데이터가 없습니다.</Text>
              </Grid>
            ) : (
              list.map((li) => (
                <Fragment key={li.id}>
                  <Grid
                    isNoWrap
                    gap='10px'
                    justify='space-between'
                    align='center'
                    marginTop='16px'
                    padding='14px 20px'
                    borderRadius='8px'
                    style={{
                      boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)',
                      cursor: 'pointer',
                    }}
                    sm={{ display: 'none' }}
                  >
                    <Grid width='10%' isInline onClick={() => history.push(`/case/library/${li.id}`)}>
                      <Grid>
                        <Text size='0.9rem'>회사</Text>
                      </Grid>
                      <Grid>
                        <Text size='1.3rem'>{li.company_tier || '-'}</Text>
                      </Grid>
                    </Grid>
                    <Grid isInline width='50%' onClick={() => history.push(`/case/library/${li.id}`)}>
                      <Grid>
                        <Text size='0.9rem'>제목</Text>
                      </Grid>
                      <Grid>
                        <Text size='1.3rem' ellipsis={1}>
                          {li.title || '-'}
                        </Text>
                      </Grid>
                    </Grid>
                    <Grid width='10%' isInline onClick={() => history.push(`/case/library/${li.id}`)}>
                      <Grid>
                        <Text size='0.9rem'>구분</Text>
                      </Grid>
                      <Grid>
                        <Text size='1.3rem'>{li.hired_type_text || '-'}</Text>
                      </Grid>
                    </Grid>
                    <Grid width='10%' isInline onClick={() => history.push(`/case/library/${li.id}`)}>
                      <Grid>
                        <Text size='0.9rem'>기출 시기</Text>
                      </Grid>
                      <Grid>
                        <Text size='1.3rem'>{li.interview_date || '-'}</Text>
                      </Grid>
                    </Grid>
                    <Grid width='7%' isInline onClick={() => history.push(`/case/library/${li.id}`)}>
                      <Grid justify='center'>
                        <Text size='0.9rem'>조회수</Text>
                      </Grid>
                      <Grid justify='center'>
                        <Text size='1.3rem'>{li.hit_count ?? '-'}</Text>
                      </Grid>
                    </Grid>
                    <Grid width='7%' isInline onClick={() => history.push(`/case/library/${li.id}`)}>
                      <Grid justify='center'>
                        <Text size='0.9rem'>추천</Text>
                      </Grid>
                      <Grid justify='center'>
                        <Text size='1.3rem'>{li.good_count ?? '-'}</Text>
                      </Grid>
                    </Grid>
                    <Grid width='5%' isInline justify='center' align='center' onClick={() => bookmark(li.id)}>
                      <Grid
                        isInline
                        image={li.is_bookmark ? CheckedSource : UncheckedSource}
                        width='15px'
                        height='20px'
                      />
                    </Grid>
                  </Grid>
                  <Grid
                    display='none'
                    isNoWrap
                    gap='5px'
                    justify='space-between'
                    align='center'
                    marginTop='12px'
                    padding='8px 11px'
                    borderRadius='8px'
                    style={{
                      boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)',
                      cursor: 'pointer',
                    }}
                    sm={{ display: 'flex' }}
                  >
                    <Grid width='28%' isInline onClick={() => history.push(`/case/library/${li.id}`)}>
                      <Grid>
                        <Text ellipsis={1}>{li.company_tier || '-'}</Text>
                      </Grid>
                      <Grid gap='5px' marginTop='8px'>
                        <Text size='0.7rem'>{li.interview_date || '-'}</Text>
                        <Text size='0.7rem'>{li.hired_type_text || '-'}</Text>
                      </Grid>
                    </Grid>
                    <Grid isInline width='55%' onClick={() => history.push(`/case/library/${li.id}`)}>
                      <Grid>
                        <Text ellipsis={1}>{li.title || '-'}</Text>
                      </Grid>
                      <Grid isInline gap='4px' marginTop='8px'>
                        <Text size='0.7rem'>조회수</Text>
                        <Text size='0.7rem'>{li.hit_count ?? '-'}</Text>
                      </Grid>
                      <Grid isInline gap='4px' margin='8px 0 0 16px'>
                        <Text size='0.7rem'>추천</Text>
                        <Text size='0.7rem'>{li.good_count ?? '-'}</Text>
                      </Grid>
                    </Grid>
                    <Grid width='10%' isInline align='center' onClick={() => bookmark(li.id)}>
                      <Grid
                        isInline
                        image={li.is_bookmark ? CheckedSource : UncheckedSource}
                        width='15px'
                        height='20px'
                      />
                    </Grid>
                  </Grid>
                </Fragment>
              ))
            )}
          </Grid>
          <Grid marginTop='40px' justify='center'>
            <Pagination current={page} pageSize={limit} responsive total={total} onChange={setPage} />
          </Grid>
        </Grid>
      </Grid>

      <MentorFilterModal modalState={mentorFilterModal} setModalState={setMentorFilterModal} />
    </>
  );
};

export default Library;
